import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import { FilesService } from '../../../core/services/files.service';
import { SupplierService } from '../../../core/services/supplier.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable } from 'rxjs';
import { IDeactivateComponent } from '../../../core/guards/deactivate.gaurd';

const SUPPLIERS_PAYMENT_UPLOAD = 'SUPPLIERS_PAYMENT_UPLOAD';

@Component({
  selector: 'app-add-edit-supplier',
  templateUrl: './add-edit-supplier.component.html',
  styleUrls: ['./add-edit-supplier.component.scss'],
})
export class AddEditSupplierComponent implements OnInit, IDeactivateComponent {
  suppliersForm: FormGroup | any;
  formErrors: any;
  paymentFiles: any[] = [];
  breadcrumbs: any[] = [];
  currentEmail = '';
  supplierId: any;
  status = 'create';
  emailExists = false;
  supplierData = {
    paymentAttachment: [
      {
        name: '',
        url: '',
      },
    ],
    supplierDetails: {
      name: '',
      email: '',
      mobileNo: '',
    },
    paymentDetails: {
      accountNo: '',
      accountType: '',
      bankName: '',
      branch: '',
      holderName: '',
    },
  };
  prevPage = '';
  paymentFilesUploading = false;
  submit = false;
  loading = false;

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): string | undefined {
    const confirmationMessage =
      'You have unsaved changes. Do you really want to leave?';
    $event.returnValue = confirmationMessage; // Standard for most browsers
    return confirmationMessage; // For some older browsers
  }

  constructor(
    private fb: FormBuilder,
    private filesService: FilesService,
    private supplierService: SupplierService,
    private router: Router,
    private route: ActivatedRoute,
    private message: NzMessageService,
  ) {}

  ngOnInit(): void {
    this.uploadLoader();
    this.initSupplierForm();
    this.paymentFiles = [];
    this.route.queryParams.subscribe((params) => {
      this.prevPage = params['prev'];
    });
    this.supplierId = this.route.snapshot.paramMap.get('id');
    if (this.supplierId) {
      this.loading = true;
      this.supplierService.getSuppliersById(this.supplierId).then((res) => {
        this.status = 'edit';
        this.supplierData = res;
        this.currentEmail = res.supplierDetails?.email;
        this.paymentFiles = res.paymentAttachment;
        this.suppliersForm.patchValue(this.supplierData);
        this.setBreadcrumbs();
        this.loading = false;
      });
    }
    this.setBreadcrumbs();
  }

  uploadLoader() {
    this.filesService.fileUploadingSubject.subscribe((res: any) => {
      switch (res.type) {
        case SUPPLIERS_PAYMENT_UPLOAD:
          this.paymentFilesUploading = res.loading;
          break;
      }
    });
  }

  initSupplierForm(): void {
    this.suppliersForm = this.fb.group({
      supplierDetails: this.fb.group({
        name: [
          null,
          [
            Validators.required,
            Validators.maxLength(70),
            Validators.pattern(/^[a-zA-Z\s]*$/),
          ],
        ],
        mobileNo: [
          null,
          [
            Validators.required,
            Validators.maxLength(15),
            Validators.pattern(/^\d+$/),
          ],
        ],
        email: [
          null,
          [Validators.required, Validators.maxLength(100), Validators.email],
        ],
      }),
      paymentDetails: this.fb.group({
        bankName: [null],
        branch: [null],
        accountType: [null],
        holderName: [
          this.supplierData.supplierDetails.name,
          [Validators.maxLength(70), Validators.pattern(/^[a-zA-Z\s]*$/)],
        ],
        accountNo: [
          null,
          [Validators.maxLength(70), Validators.pattern(/^\d+$/)],
        ],
      }),
      notes: this.fb.group({
        note: [null, []],
      }),
    });
  }

  initFormErrors() {
    this.formErrors = {
      supplierDetails: {
        name: {},
        mobileNo: {},
        email: {},
      },
      paymentDetails: {
        bankName: {},
        branch: {},
        accountType: {},
        holderName: {},
        accountNo: {},
      },
      notes: {
        note: {},
      },
    };
  }

  setBreadcrumbs(): void {
    if (this.status === 'edit') {
      this.breadcrumbs = [
        {
          title: ` Suppliers `,
          route: '/admin/suppliers',
          icon: 'delivered-procedure',
        },
        {
          title: ` Edit `,
        },
      ];
    } else {
      this.breadcrumbs = [
        {
          title: ` Suppliers `,
          route: '/admin/suppliers',
          icon: 'delivered-procedure',
        },
        {
          title: ` Add `,
        },
      ];
    }
  }

  upload = (item: NzUploadXHRArgs) =>
    this.filesService
      .postFile(item.file, SUPPLIERS_PAYMENT_UPLOAD)
      .subscribe((res) => {
        this.message.create('success', `Attachment has been uploaded!`);
        const data = res as any;
        this.paymentFiles.push({ name: item.file.name, url: data.url });
        this.filesService.fileUploadingSubject.next({
          type: SUPPLIERS_PAYMENT_UPLOAD,
          loading: false,
        });
      });

  onSubmit(): void {
    if (this.suppliersForm.valid) {
      this.supplierService
        .getSupplierByEmail({
          email: this.suppliersForm.get('supplierDetails.email').value,
        })
        .then((res) => {
          if (res) {
            this.message.create('warning', `Email is already existing!`);
            this.emailExists = true;
          } else {
            this.emailExists = false;
            this.supplierService
              .createSupplier({
                ...this.suppliersForm.value,
                paymentAttachment: this.paymentFiles,
              })
              .then(() => {
                this.submit = true;
                this.message.create('success', `New supplier has been added!`);
                if (this.prevPage === 'add-drivers') {
                  this.router.navigate(['admin/drivers/add-edit']);
                } else {
                  this.router.navigate(['admin/suppliers']);
                }
              });
          }
        });
    } else {
      this.markFormGroupAsTouched(this.suppliersForm);
    }
  }

  markFormGroupAsTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control: any) => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupAsTouched(control);
      }
    });
  }

  onUpdate(): void {
    if (this.suppliersForm.valid) {
      this.supplierService
        .getSupplierByEmail({
          email: this.suppliersForm.get('supplierDetails.email').value,
        })
        .then((res) => {
          if (
            res &&
            this.currentEmail !==
              this.suppliersForm.get('supplierDetails.email').value
          ) {
            console.log(res);
            this.message.create('warning', `Email is already existing!`);
            this.emailExists = true;
          } else {
            this.supplierService
              .updateSuppliers({
                _id: this.supplierId,
                ...this.suppliersForm.value,
                paymentAttachment: this.paymentFiles,
              })
              .then((resp) => {
                this.submit = true;
                this.message.create('success', `Supplier has been updated!`);
                if (this.prevPage) {
                  this.router.navigate([`admin/suppliers/${this.supplierId}`]);
                } else {
                  this.router.navigate(['admin/suppliers']);
                }
              });
          }
        });
    } else {
      this.markFormGroupAsTouched(this.suppliersForm);
    }
  }

  cancel(): void {}

  confirm(): void {
    if (this.prevPage) {
      this.router.navigate([`admin/suppliers/${this.supplierId}`]);
    } else {
      this.router.navigate(['admin/suppliers']);
    }
  }

  confirmRemoveAttachment(attachmentId: any): void {
    this.paymentFiles.splice(attachmentId, 1);
    this.message.create('success', `Attachment has been deleted!`);
  }

  updateAccountHolderName() {
    if (!this.supplierId) {
      this.suppliersForm
        .get('paymentDetails.holderName')
        .setValue(this.suppliersForm.get('supplierDetails.name').value);
    }
  }

  canExit(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.submit) {
      return true;
    } else if (this.suppliersForm.dirty || this.suppliersForm.touched) {
      return confirm(
        ' You have unsaved changes, Do you really want to discard these changes?',
      );
    } else {
      return true;
    }
  }
}
